import { Action, State, StateContext, Store } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { interval, of, Subject, takeUntil, tap } from 'rxjs';
import { AccountingApiService } from '../../_services/accounting-service/accounting-api/accounting-api.service';
import { AccountingStateModel } from './accounting.models';
import { AccountConnectionDetail, AccountingConnectionStatus } from '../../_interfaces/accounting.interface';
import {
  ClearAccountingState,
  LoadTaxRates,
  LoadTrackingCategories,
  SetAccountingProviderStateFromAccountingConnection,
  SetAccountingProviderStateFromSelectingSourceAccount,
  SetAccountingProviderStateFromSelectingTenant,
  SetAccountingProviderStateFromTransactionSyncFailure,
  StartPollingAccountingConnectionStatus,
  StopPollingAccountingConnectionStatus,
} from './accounting.actions';
import { SubscriptionSelectors } from '../subscription/subscription.selectors';
import { ToastService } from '../../_services/toast-service/toast.service';

@State<AccountingStateModel>({
  name: 'accounting',
  defaults: {
    provider: undefined,
    connectionStatus: undefined,
    bank_accounts_created: undefined,
    tenant: undefined,
    source_account: undefined,
    sync_enabled: undefined,
    trackingCategories: [],
    trackingCategoriesLastFetched: null,
    taxRates: [],
    taxRatesLastFetched: null,
  },
})
@Injectable()
export class AccountingState {
  readonly accountingApiService = inject(AccountingApiService);
  readonly toastService = inject(ToastService);
  readonly store = inject(Store);
  readonly hasAccounting$ = this.store.select(SubscriptionSelectors.hasAccounting);

  private readonly POLL_INTERVAL = 1000; // 1 second
  public readonly stopPolling$ = new Subject<boolean>();

  /**
   * Checks if a timestamp is from the same calendar day as now.
   * Used for cache validation since accounting data syncs at midnight.
   */
  private isSameDay(timestamp: number, now: Date = new Date()): boolean {
    const cachedDate = new Date(timestamp);
    return (
      cachedDate.getDate() === now.getDate() &&
      cachedDate.getMonth() === now.getMonth() &&
      cachedDate.getFullYear() === now.getFullYear()
    );
  }

  queryStatus({ patchState }: StateContext<AccountingStateModel>) {
    return this.accountingApiService.getAccountConnectionDetails().subscribe({
      next: (res) => {
        if (res) {
          patchState({ connectionStatus: res.status });
          patchState({ provider: res.provider });
          if (res.status !== AccountingConnectionStatus.Pending && res.status !== AccountingConnectionStatus.HasToken) {
            this.stopPolling$.next(true);
          }
        } else {
          this.stopPolling$.next(true);
        }
      },
      error: () => {
        patchState({ connectionStatus: AccountingConnectionStatus.Error });
        this.stopPolling$.next(true);
      },
    });
  }

  @Action([SetAccountingProviderStateFromSelectingTenant, SetAccountingProviderStateFromSelectingSourceAccount])
  setAccountingConnectionDetails({ patchState }: StateContext<AccountingStateModel>) {
    return this.accountingApiService.getAccountConnectionDetails().pipe(
      tap((data: AccountConnectionDetail) => {
        if (data) {
          patchState({
            connectionStatus: data.status,
            provider: data.provider,
            bank_accounts_created: data.bank_accounts_created,
            tenant: data.tenant,
            source_account: data.source_account,
            sync_enabled: data.sync_enabled,
          });
        }
      }),
    );
  }

  @Action([SetAccountingProviderStateFromAccountingConnection, SetAccountingProviderStateFromTransactionSyncFailure])
  setAccountingProviderDetails({ patchState }: StateContext<AccountingStateModel>) {
    // Use selectSnapshot to read the current subscription state synchronously
    // This ensures we get the latest value even when called immediately after
    // subscription state is updated (avoiding signal update timing issues)
    const hasAccounting = this.store.selectSnapshot(SubscriptionSelectors.hasAccounting);
    if (!hasAccounting) return undefined;

    return this.accountingApiService.getAccountConnectionDetails().pipe(
      tap((data: AccountConnectionDetail) => {
        if (data) {
          patchState({
            connectionStatus: data.status,
            provider: data.provider,
            bank_accounts_created: data.bank_accounts_created,
            tenant: data.tenant,
            source_account: data.source_account,
            sync_enabled: data.sync_enabled,
          });

          // Refresh tracking categories and tax rates when accounting connection is established/updated
          // This ensures we have fresh data after connecting or reconnecting
          this.store.dispatch(new LoadTrackingCategories(true));
          this.store.dispatch(new LoadTaxRates(true));
        }
      }),
    );
  }

  @Action(StartPollingAccountingConnectionStatus)
  startPollingAccountingConnectionStatus(ctx: StateContext<AccountingStateModel>) {
    interval(this.POLL_INTERVAL)
      .pipe(takeUntil(this.stopPolling$))
      .subscribe(() => {
        this.queryStatus(ctx);
      });
  }

  @Action(StopPollingAccountingConnectionStatus)
  stopPollingAccountingConnectionStatus() {
    this.stopPolling$.next(true);
  }

  @Action(ClearAccountingState)
  clearAccountingState({ setState }: StateContext<AccountingStateModel>) {
    setState({
      provider: undefined,
      connectionStatus: undefined,
      bank_accounts_created: undefined,
      tenant: undefined,
      source_account: undefined,
      sync_enabled: undefined,
      trackingCategories: [],
      trackingCategoriesLastFetched: null,
      taxRates: [],
      taxRatesLastFetched: null,
    });
  }

  @Action(LoadTrackingCategories)
  loadTrackingCategories({ getState, patchState }: StateContext<AccountingStateModel>, action: LoadTrackingCategories) {
    const state = getState();
    const now = new Date();

    // Check if cache is from the same calendar day
    // Tracking categories sync at midnight, so cache is valid only for the current day
    const isCacheValid =
      !action.forceRefresh &&
      state.trackingCategoriesLastFetched !== null &&
      this.isSameDay(state.trackingCategoriesLastFetched, now) &&
      state.trackingCategories.length > 0;

    if (isCacheValid) {
      return of(undefined);
    }

    // Fetch fresh data from API (new day or no cache)
    return this.accountingApiService.getAccountingTrackingCategories().pipe(
      tap((trackingCategories) => {
        patchState({
          trackingCategories,
          trackingCategoriesLastFetched: Date.now(),
        });
      }),
    );
  }

  @Action(LoadTaxRates)
  loadTaxRates({ getState, patchState }: StateContext<AccountingStateModel>, action: LoadTaxRates) {
    const state = getState();
    const now = new Date();

    // Check if cache is from the same calendar day
    // Tax rates sync at midnight, so cache is valid only for the current day
    const isCacheValid =
      !action.forceRefresh &&
      state.taxRatesLastFetched !== null &&
      this.isSameDay(state.taxRatesLastFetched, now) &&
      state.taxRates.length > 0;

    if (isCacheValid) {
      return of(undefined);
    }

    // Fetch fresh data from API (new day or no cache)
    return this.accountingApiService.getAccountingTaxRates().pipe(
      tap((taxRates) => {
        patchState({
          taxRates,
          taxRatesLastFetched: Date.now(),
        });
      }),
    );
  }
}
